/**
 * Occurrence math for schedules. All instants are UTC; schedules describe
 * local wall-clock times in an IANA timezone.
 */

export type Cadence = "daily" | "weekly" | "monthly";

export interface ScheduleSpec {
  cadence: Cadence;
  time_of_day: string; // HH:MM
  day_of_week: number | null; // 0..6, Sunday = 0
  day_of_month: number | null; // 1..31
  timezone: string;
}

export interface LocalDate {
  y: number;
  m: number; // 1..12
  d: number; // 1..31
}

const DAY_MS = 86_400_000;

function partsIn(date: Date, tz: string): { y: number; m: number; d: number; h: number; min: number; s: number } {
  const fmt = new Intl.DateTimeFormat("en-US", {
    timeZone: tz,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = fmt.formatToParts(date);
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? "0");
  return { y: get("year"), m: get("month"), d: get("day"), h: get("hour") % 24, min: get("minute"), s: get("second") };
}

/** Converts a wall-clock time in `tz` to a UTC Date. Handles DST by iterating on the offset. */
export function zonedToUtc(local: LocalDate, hour: number, minute: number, tz: string): Date {
  const want = Date.UTC(local.y, local.m - 1, local.d, hour, minute, 0);
  let guess = want;
  for (let i = 0; i < 3; i++) {
    const p = partsIn(new Date(guess), tz);
    const asIfUtc = Date.UTC(p.y, p.m - 1, p.d, p.h, p.min, p.s);
    const offset = asIfUtc - guess; // zone offset from UTC at `guess`
    const next = want - offset;
    if (next === guess) break;
    guess = next;
  }
  return new Date(guess);
}

export function localDateOf(instant: Date, tz: string): LocalDate {
  const p = partsIn(instant, tz);
  return { y: p.y, m: p.m, d: p.d };
}

function addDays(ld: LocalDate, n: number): LocalDate {
  const t = new Date(Date.UTC(ld.y, ld.m - 1, ld.d) + n * DAY_MS);
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function daysInMonth(y: number, m: number): number {
  return new Date(Date.UTC(y, m, 0)).getUTCDate();
}

function weekday(ld: LocalDate): number {
  return new Date(Date.UTC(ld.y, ld.m - 1, ld.d)).getUTCDay();
}

export function parseTime(t: string): { hour: number; minute: number } {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t);
  if (!m) throw new Error(`Bad time_of_day: ${t}`);
  const hour = Number(m[1]);
  const minute = Number(m[2]);
  if (hour > 23 || minute > 59) throw new Error(`Bad time_of_day: ${t}`);
  return { hour, minute };
}

/** Whether the schedule fires on this local calendar date. */
function firesOn(spec: ScheduleSpec, ld: LocalDate): boolean {
  switch (spec.cadence) {
    case "daily":
      return true;
    case "weekly":
      return weekday(ld) === (spec.day_of_week ?? 0);
    case "monthly": {
      const want = Math.min(spec.day_of_month ?? 1, daysInMonth(ld.y, ld.m));
      return ld.d === want;
    }
  }
}

/** Latest occurrence with time <= `at`. Searches back up to ~2 months. */
export function latestOccurrenceAtOrBefore(spec: ScheduleSpec, at: Date): Date | null {
  const { hour, minute } = parseTime(spec.time_of_day);
  let ld = localDateOf(at, spec.timezone);
  for (let i = 0; i < 70; i++) {
    if (firesOn(spec, ld)) {
      const t = zonedToUtc(ld, hour, minute, spec.timezone);
      if (t.getTime() <= at.getTime()) return t;
    }
    ld = addDays(ld, -1);
  }
  return null;
}

/** Earliest occurrence with time > `after`. Searches forward up to ~2 months. */
export function nextOccurrenceAfter(spec: ScheduleSpec, after: Date): Date | null {
  const { hour, minute } = parseTime(spec.time_of_day);
  let ld = localDateOf(after, spec.timezone);
  for (let i = 0; i < 70; i++) {
    if (firesOn(spec, ld)) {
      const t = zonedToUtc(ld, hour, minute, spec.timezone);
      if (t.getTime() > after.getTime()) return t;
    }
    ld = addDays(ld, 1);
  }
  return null;
}

/**
 * The single occurrence that should be materialized now, if any: the latest
 * one that is due (<= now) and newer than the last materialization point.
 * Only one occurrence per pass, so a long-idle schedule does not flood the
 * board with back-dated tasks.
 */
export function dueOccurrence(spec: ScheduleSpec, lastMaterializedAt: Date, now: Date): Date | null {
  const t = latestOccurrenceAtOrBefore(spec, now);
  if (!t) return null;
  return t.getTime() > lastMaterializedAt.getTime() ? t : null;
}

/**
 * A schedule needs a before photo, as any task does; its chores carry it (docs/product.md,
 * "Schedules (2026-10-01)"). The routes refuse a create without one, and an edit that would
 * leave the schedule without one, with this message.
 */
export const SCHEDULE_PHOTO_REQUIRED = "Add a before photo";

// ---------- Calendar months in a zone (the Hall of Fame, the crown) ----------

/** Whether `tz` is an IANA zone this runtime knows. */
export function isTimeZone(tz: unknown): tz is string {
  if (typeof tz !== "string" || tz.length === 0 || tz.length > 64) return false;
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: tz });
    return true;
  } catch {
    return false;
  }
}

/** One calendar month in a zone: its "YYYY-MM" key and its bounds as ISO UTC, start inclusive, end exclusive. */
export interface ZonedMonth {
  key: string;
  start: string;
  end: string;
}

/** The calendar month `offset` months from the one holding `instant`, in `tz` (0 that month, -1 the one before). */
export function zonedMonth(instant: Date, tz: string, offset = 0): ZonedMonth {
  const { y, m } = localDateOf(instant, tz);
  const first = new Date(Date.UTC(y, m - 1 + offset, 1));
  const next = new Date(Date.UTC(y, m + offset, 1));
  const start = zonedToUtc({ y: first.getUTCFullYear(), m: first.getUTCMonth() + 1, d: 1 }, 0, 0, tz);
  const end = zonedToUtc({ y: next.getUTCFullYear(), m: next.getUTCMonth() + 1, d: 1 }, 0, 0, tz);
  const key = `${first.getUTCFullYear()}-${String(first.getUTCMonth() + 1).padStart(2, "0")}`;
  return { key, start: start.toISOString(), end: end.toISOString() };
}

/** Every calendar month in `tz` from the one holding `from` to the one holding `to`, oldest first. */
export function zonedMonthsBetween(from: Date, to: Date, tz: string): ZonedMonth[] {
  const out: ZonedMonth[] = [];
  if (!(from.getTime() <= to.getTime())) return out;
  for (let i = 0; i < 1200; i++) {
    const month = zonedMonth(from, tz, i);
    out.push(month);
    if (Date.parse(month.end) > to.getTime()) break;
  }
  return out;
}
